import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOperator } from '@/lib/operator-context';
import { getAuditLogs } from '@/lib/operator-api';
import type { AuditLogEntry, AuditLogFilters } from '@/lib/operator-api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Link } from '@tanstack/react-router';
import { ShieldCheck } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';

const ACTIONS = [
  'VIEW_CONVERSATION',
  'VIEW_CUSTOMER',
  'SEARCH_CUSTOMER',
  'EXPORT_DATA',
  'LOGIN',
  'LOGOUT',
];

const RESOURCE_TYPES = [
  'CONVERSATION',
  'CUSTOMER',
  'BOOKING',
  'PAYMENT',
  'AUDIT_LOG',
];

function OperatorAuditLogsPage() {
  const { t } = useTranslation();
  const { operator, permissions, isLoading: authLoading } = useOperator();
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Filters
  const [action, setAction] = useState('');
  const [resourceType, setResourceType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const limit = 50;

  useEffect(() => {
    if (!operator || !permissions?.canViewAuditLogs) return;
    const controller = new AbortController();

    const fetchLogs = async () => {
      setLoading(true);
      setError(null);

      try {
        const filters: AuditLogFilters = {
          action: action || undefined,
          resourceType: resourceType || undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          page: currentPage,
          limit,
        };

        const result = await getAuditLogs(filters);
        if (controller.signal.aborted) return;
        setLogs(result.logs || []);
        setTotal(result.total || 0);
        setTotalPages(Math.ceil((result.total || 0) / limit));
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Failed to fetch audit logs');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchLogs();
    return () => controller.abort();
  }, [operator, permissions, action, resourceType, dateFrom, dateTo, currentPage]);

  const handleReset = () => {
    setAction('');
    setResourceType('');
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getActionBadgeVariant = (actionType: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    if (actionType.includes('VIEW')) return 'secondary';
    if (actionType.includes('EXPORT')) return 'destructive';
    if (actionType.includes('LOGIN')) return 'default';
    return 'outline';
  };

  if (authLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </CardContent>
      </Card>
    );
  }

  if (!operator) {
    return null;
  }

  if (!permissions?.canViewAuditLogs) {
    return (
      <Card className="text-center py-12">
        <CardContent className="space-y-4">
          <div className="text-4xl">🔒</div>
          <h2 className="text-xl font-semibold">{t('common.accessDenied')}</h2>
          <p className="text-muted-foreground">
            {t('common.noPermission', { role: operator.role, action: t('auditLogs.viewAuditLogs') })}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('auditLogs.title')}</h1>
          <p className="text-muted-foreground">
            {t('auditLogs.subtitle')}
          </p>
        </div>
      </div>

      <Link
        to="/operator/compliance"
        className="flex items-center gap-3 rounded-lg border bg-card p-4 transition-colors hover:bg-accent"
      >
        <ShieldCheck className="h-5 w-5 text-emerald-600" />
        <div>
          <p className="font-medium">WhatsApp Compliance</p>
          <p className="text-sm text-muted-foreground">Monitor rate limits, messaging windows, and compliance status</p>
        </div>
      </Link>

      {error && (
        <div className="rounded-md bg-destructive/10 p-4 text-destructive">
          {error}
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>{t('common.filters')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <div className="space-y-2">
              <Label htmlFor="action">{t('auditLogs.actionColumn')}</Label>
              <Select
                value={action}
                onValueChange={(value) => {
                  setAction(value === 'all' ? '' : value);
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger id="action">
                  <SelectValue placeholder={t('auditLogs.allActions')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('auditLogs.allActions')}</SelectItem>
                  {ACTIONS.map((a) => (
                    <SelectItem key={a} value={a}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="resourceType">{t('auditLogs.resourceType')}</Label>
              <Select
                value={resourceType}
                onValueChange={(value) => {
                  setResourceType(value === 'all' ? '' : value);
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger id="resourceType">
                  <SelectValue placeholder={t('auditLogs.allTypes')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('auditLogs.allTypes')}</SelectItem>
                  {RESOURCE_TYPES.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="dateFrom">{t('common.fromDate')}</Label>
              <Input
                id="dateFrom"
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dateTo">{t('common.toDate')}</Label>
              <Input
                id="dateTo"
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <div className="flex items-end">
              <Button variant="outline" onClick={handleReset} className="w-full">
                {t('common.reset')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {loading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </CardContent>
        </Card>
      ) : logs.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="text-4xl mb-4">📋</div>
            <h3 className="text-lg font-semibold mb-2">{t('auditLogs.noLogsFound')}</h3>
            <p className="text-muted-foreground">
              {action || resourceType || dateFrom || dateTo
                ? t('common.tryAdjustingFilters')
                : t('auditLogs.logsWillAppear')}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <div className="mb-4">
              <span className="text-muted-foreground">{total} audit log entries</span>
            </div>

            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('auditLogs.timestamp')}</TableHead>
                    <TableHead>{t('auditLogs.operator')}</TableHead>
                    <TableHead>{t('auditLogs.actionColumn')}</TableHead>
                    <TableHead>{t('auditLogs.resource')}</TableHead>
                    <TableHead>{t('auditLogs.piiAccessed')}</TableHead>
                    <TableHead>{t('auditLogs.ipAddress')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="text-xs font-mono text-muted-foreground">
                        {formatDate(log.timestamp)}
                      </TableCell>
                      <TableCell>
                        <div className="font-semibold">{log.operatorName}</div>
                        <div className="text-xs text-muted-foreground">{log.operatorId}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getActionBadgeVariant(log.action)}>
                          {log.action}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div>{log.resourceType}</div>
                        {log.resourceId && (
                          <div className="text-xs font-mono text-muted-foreground">
                            {log.resourceId.substring(0, 8)}...
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {log.piiFieldsAccessed.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {log.piiFieldsAccessed.map((field) => (
                              <Badge
                                key={field}
                                variant="outline"
                                className="text-xs"
                              >
                                {field}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">{t('common.none')}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs font-mono text-muted-foreground">
                        {log.ipAddress || t('common.none')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(1)}
                >
                  {t('common.first')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  {t('common.previous')}
                </Button>

                <span className="mx-4 text-sm text-muted-foreground">
                  {t('common.pageOf', { current: currentPage, total: totalPages })}
                </span>

                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  {t('common.next')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(totalPages)}
                >
                  {t('common.last')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export const Route = createFileRoute('/_auth/operator/audit-logs')({
  component: OperatorAuditLogsPage,
});
